import type { PropsWithChildren } from "react";

import { ModalTestID, TextTestID } from "@/constants/TestID";
import { TextSeparator } from "@/constants/Text";
import BottomSheetModal from "@/ui/composed/modal/BottomSheetModal";
import { Button, FAIconName, Icon, Text } from "@/ui/core";
import { Box, HStack, VStack } from "@/ui/layout";
import { IllustrationSize } from "@/ui/theme/icons/illustrations/groupClasses/IllustrationDefinitions";
import MegaphoneIllustrationSVG from "@/ui/theme/icons/illustrations/groupClasses/MegaphoneIllustrationSVG";
import { ColorToken } from "@/ui/theme/tokens";
import { joinTestIDs } from "@/utils/string.util";

enum WebRedesignOverviewModalText {
  ButtonTitle = "Got it",
  DismissAccessibilityLabel = "Dismiss Web Redesign Overview",
  Title = "Your web experience got a refresh to match the mobile app!",
}

enum WebRedesignOverviewModalTestID {
  BulletExploreGroups = "bullet-explore-groups",
  BulletJournal = "bullet-journal",
  BulletVideoResources = "bullet-video-resources",
  DismissButton = "dismiss",
}

const InlineIconSize = 24;

interface WebRedesignOverviewModalProps {
  isOpen: boolean;
  onRequestClose: () => void;
}

const WebRedesignOverviewModal = ({
  isOpen,
  onRequestClose,
}: WebRedesignOverviewModalProps) => {
  const baseTestID = ModalTestID.WebRedesignOverview;

  return (
    <BottomSheetModal
      dismissAccessibilityLabel={
        WebRedesignOverviewModalText.DismissAccessibilityLabel
      }
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      testID={baseTestID}
    >
      <VStack alignItems="center" gap={24} paddingVertical={16}>
        <MegaphoneIllustrationSVG
          height={IllustrationSize.Large}
          width={IllustrationSize.Large}
        />
        <Text testID={joinTestIDs(baseTestID, TextTestID.Title)} variant="h5">
          {WebRedesignOverviewModalText.Title}
        </Text>
        <BulletList testID={baseTestID} />
        <HStack justifyContent="center" width="100%">
          <Button
            isFullWidth
            onPress={onRequestClose}
            testID={joinTestIDs(
              baseTestID,
              WebRedesignOverviewModalTestID.DismissButton,
            )}
            title={WebRedesignOverviewModalText.ButtonTitle}
          />
        </HStack>
      </VStack>
    </BottomSheetModal>
  );
};

const BulletList = ({ testID: baseTestID }: ViewWithTestID) => (
  <VStack gap={16} paddingHorizontal={8}>
    <BulletPoint
      testID={joinTestIDs(
        baseTestID,
        WebRedesignOverviewModalTestID.BulletExploreGroups,
      )}
    >
      <Text testID={joinTestIDs(baseTestID, "explore-groups")}>
        {"Navigate to the "}
        <Text
          testID={joinTestIDs(baseTestID, "explore-groups-prefix")}
          variant="bodyMedium"
        >
          Explore
        </Text>
        {" screen to find "}
        <Text
          testID={joinTestIDs(baseTestID, "explore-groups-suffix")}
          variant="bodyMedium"
        >
          Groups
        </Text>
      </Text>
    </BulletPoint>

    <BulletPoint
      testID={joinTestIDs(
        baseTestID,
        WebRedesignOverviewModalTestID.BulletVideoResources,
      )}
    >
      <Text testID={joinTestIDs(baseTestID, "video-resources")}>
        {"Browse through new-to-web "}
        <Text
          testID={joinTestIDs(baseTestID, "video-resources-suffix")}
          variant="bodyMedium"
        >
          video resources
        </Text>
        , also on the Explore screen
      </Text>
    </BulletPoint>

    <BulletPoint
      testID={joinTestIDs(
        baseTestID,
        WebRedesignOverviewModalTestID.BulletJournal,
      )}
    >
      <JournalBulletContent baseTestID={baseTestID} />
    </BulletPoint>
  </VStack>
);

const JournalBulletContent = ({ baseTestID }: { baseTestID: string }) => (
  <HStack alignItems="center" flexWrap="wrap" gap={4}>
    <Text testID={joinTestIDs(baseTestID, "journal-prefix")}>
      If you are an Equip Journal user, you can now log entries from the web
      using the
    </Text>
    <Box
      alignItems="center"
      backgroundColor={ColorToken.AccentBlue}
      borderRadius={InlineIconSize / 2}
      height={InlineIconSize}
      justifyContent="center"
      width={InlineIconSize}
    >
      <Icon
        color={ColorToken.NeutralBackgroundPrimary}
        name={FAIconName.Plus}
        size="sm"
        testID={joinTestIDs(baseTestID, "plus")}
      />
    </Box>
    <Text testID={joinTestIDs(baseTestID, "journal-suffix")}>
      button in the righthand corner
    </Text>
  </HStack>
);

type BulletPointProps = PropsWithChildren<ViewWithTestID>;

const BulletPoint = ({ children, testID }: BulletPointProps) => (
  <HStack gap={8} testID={testID}>
    <Text color={ColorToken.AccentBlue} testID={joinTestIDs(testID, "dot")}>
      {TextSeparator.Dot}
    </Text>
    <Box flex={1} flexShrink={1}>
      {children}
    </Box>
  </HStack>
);

export type { WebRedesignOverviewModalProps };
export { WebRedesignOverviewModalTestID, WebRedesignOverviewModalText };
export default WebRedesignOverviewModal;
